import { Box, Typography } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import {
  FundingProgramButton,
  useShowCredits,
  useShowFundingProgramButton
} from "@outschool/ui-components-website";
import _ from "lodash";
import React, { useState } from "react";

import AgeFilters from "../../search/AgeFilters";
import CapacityFilter from "../../search/CapacityFilter";
import CreditsPerMeetingFilter from "../../search/CreditsPerMeetingFilter";
import DateFilters from "../../search/DateFilters";
import FilterActionButtons from "../../search/desktop/FilterActionButtons";
import UncommonFilters from "../../search/mobile/filters/UncommonFilters";
import PricePerMeetingFilter from "../../search/PricePerMeetingFilter";
import TimeFilters from "../../search/TimeFilters";
import { FiltersToShowType } from "./FiltersToShow";

type CategoryListingsFiltersMobileModalProps = {
  filters: SearchFiltersType;
  setFilters: (value: SearchFiltersType) => void;
  onCancel: () => void;
  isCuratedCategoryFilter?: boolean;
  filtersToShow: FiltersToShowType;
};

export const CategoryListingsFiltersMobileModal = ({
  filters,
  onCancel,
  setFilters,
  isCuratedCategoryFilter,
  filtersToShow
}: CategoryListingsFiltersMobileModalProps) => {
  const { t } = useTranslation(
    "client\\components\\categories\\CateogryListingsFiltersMobileModal"
  );
  const [filterState, setFilterState] = useState(filters);

  const capacityFilters = _.pick(filterState, SearchFilters.CAPACITY_FILTERS);

  const pricePerMeetingFilters = _.pick(
    filterState,
    SearchFilters.PRICE_PER_MEETING_FILTERS
  );

  const dateFilters = _.pick(filterState, SearchFilters.DATE_FILTERS);

  const showFundingProgramButton = useShowFundingProgramButton(filterState);

  const showCredits = useShowCredits();

  const onChangeFilters = (newFilters: SearchFiltersType) => {
    setFilterState({ ...filterState, ...newFilters });
  };

  const onChangePrice = (newPrice: SearchFiltersType) => {
    const priceFilter = _.isEmpty(newPrice)
      ? { pricePerMeetingMax: 0 }
      : newPrice;

    const filteredState = _.omit(filterState, [
      "pricePerMeetingMin",
      "pricePerMeetingMax"
    ]);
    const { pricePerMeetingMin, pricePerMeetingMax } = priceFilter;

    let price =
      pricePerMeetingMin !== undefined
        ? { pricePerMeetingMin }
        : pricePerMeetingMax !== undefined
        ? { pricePerMeetingMax }
        : {};

    setFilterState({ ...filteredState, ...price });
  };

  const onChangeAge = (newAges: number[]) => {
    const age = newAges.length ? newAges.join(",") : "";
    setFilterState({ ...filterState, age });
  };

  const onApplyFilters = () => {
    setFilters(filterState);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        fontSize: "1em",
        paddingX: "20px"
      }}
    >
      <Typography variant="h3">{t("Filters")}</Typography>
      {showFundingProgramButton && (
        <FilterBlock>
          <FundingProgramButton
            updateSearchFilters={setFilterState}
            searchFilters={filterState}
          />
        </FilterBlock>
      )}
      <FilterBlock>
        <TimeFilters
          handleChangesExternally
          hideDaySelector={!filtersToShow.dow}
          hideTimeOfDay={!filtersToShow.time}
          onCancel={() => {}}
          onChange={onChangeFilters}
          showGroupSelection={false}
          timeSelectorSx={{ fontSize: "0.75em" }}
          titleSx={{ fontSize: "small" }}
          value={filterState}
        />
      </FilterBlock>

      {/* Date */}
      {filtersToShow.date && (
        <FilterBlock>
          <DateFilters
            handleChangesExternally
            isMobile
            onCancel={() => {}}
            onShortcutSelected={onChangeFilters}
            onChange={onChangeFilters}
            value={dateFilters}
          />
        </FilterBlock>
      )}

      {/* Class Size  */}
      {filtersToShow.classSize && (
        <FilterBlock>
          <CapacityFilter
            handleChangesExternally
            onCancel={() => {}}
            onChange={onChangeFilters}
            value={capacityFilters}
          />
        </FilterBlock>
      )}

      {/* Price  */}
      {filtersToShow.price && !showCredits && (
        <FilterBlock>
          <PricePerMeetingFilter
            handleChangesExternally
            onCancel={() => {}}
            onChange={onChangePrice}
            value={pricePerMeetingFilters}
          />
        </FilterBlock>
      )}
      {filtersToShow.price && showCredits && (
        <FilterBlock>
          <CreditsPerMeetingFilter
            handleChangesExternally
            onChange={onChangePrice}
            value={pricePerMeetingFilters}
          />
        </FilterBlock>
      )}

      {/* Age  */}
      {filtersToShow.age && (
        <FilterBlock last={!isCuratedCategoryFilter}>
          <AgeFilters
            handleChangesExternally
            listSx={{ gridTemplateColumns: "repeat(4, 1fr)" }}
            onCancel={() => {}}
            onChange={onChangeAge}
            value={
              !!filterState.age ? filterState.age?.split(",").map(Number) : []
            }
          />
        </FilterBlock>
      )}

      {/* More */}
      <UncommonFilters
        isCuratedCategoryFilter
        searchFilters={filterState}
        onChangeWithParam={(filterParam: string, newValue: string[]) => {
          let updateFilter = {};
          // @ts-ignore
          updateFilter[filterParam] = newValue.join(",");
          onChangeFilters(updateFilter);
        }}
        hideFormat={!filtersToShow.format}
        hideClassType={!filtersToShow.classType}
        hideLanguage={!filtersToShow.language}
      />

      <FilterActionButtons onApply={onApplyFilters} onCancel={onCancel} />
    </Box>
  );
};

const FilterBlock = ({ children, last = false }: $TSFixMe) => {
  return (
    <Box
      sx={{
        marginTop: "20px",
        paddingBottom: "20px",
        borderBottom: last ? "none" : "solid 1px #DDDFE3",
        fontSize: "0.75em"
      }}
    >
      {children}
    </Box>
  );
};
